import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, Route, Routes } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatGNF } from "@/constants/mcevents";
import { Plus, CalendarDays, MapPin, Ticket, Loader2, Trash2, Eye, EyeOff, ScanLine, BarChart3, Users as UsersIcon, Copy, Save, ArrowLeft, Radio } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import LiveEntryStats from "@/components/LiveEntryStats";
import { ShareButton } from "@/components/ShareButton";
import { imgSrc } from "@/lib/utils";
import { toast } from "sonner";

export default function ArtistEventsRoutes() {
  return (
    <Routes>
      <Route index element={<EventsList />} />
      <Route path="new" element={<NewEvent />} />
      <Route path=":id" element={<ManageEvent />} />
    </Routes>
  );
}

function EventsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/events/mine").then((r) => setEvents(r.data.events || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;

  return (
    <div data-testid="artist-events-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <div>
          <p className="overline mb-1">🎟️ Billetterie</p>
          <h1 className="font-display font-black text-3xl tracking-tighter">Mes événements</h1>
        </div>
        <Link to="/artist/events/new" className="btn-primary" data-testid="new-event-btn">
          <Plus className="w-4 h-4 mr-2" /> Créer un événement
        </Link>
      </div>
      {events.length === 0 ? (
        <div className="card-mc p-16 text-center">
          <Ticket className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">Vous n'avez pas encore créé d'événement.</p>
          <Link to="/artist/events/new" className="btn-primary inline-flex mt-6">Créer votre premier événement</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {events.map((ev) => (
            <Link key={ev.id} to={`/artist/events/${ev.id}`} className="card-mc overflow-hidden hover:border-foreground transition-colors" data-testid={`artist-event-${ev.id}`}>
              <div className="aspect-[16/9] bg-muted relative overflow-hidden">
                {imgSrc(ev.cover_image) ? <img src={imgSrc(ev.cover_image)} alt={ev.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Ticket className="w-8 h-8" /></div>}
                <span className={`absolute top-3 right-3 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${ev.published ? "bg-[hsl(var(--accent))] text-white" : "bg-muted text-muted-foreground"}`}>
                  {ev.published ? "Publié" : "Brouillon"}
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-display font-black text-xl tracking-tighter line-clamp-1">{ev.title}</h3>
                <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {new Date(ev.event_date).toLocaleDateString("fr-FR")}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="border border-border p-2">
                    <p className="overline mb-0.5">Vendus</p>
                    <p className="font-bold">{ev.tickets_sold}</p>
                  </div>
                  <div className="border border-border p-2">
                    <p className="overline mb-0.5">Recettes</p>
                    <p className="font-bold">{formatGNF(ev.revenue_gnf)}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function NewEvent() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", venue: "", city: "Conakry", event_date: "", cover_image: "" });
  const [types, setTypes] = useState([{ name: "Standard", price_gnf: 50000, quantity_total: 100, description: "" }]);

  const addType = () => setTypes((t) => [...t, { name: "", price_gnf: 0, quantity_total: 50, description: "" }]);
  const updType = (i, patch) => setTypes((t) => t.map((x, j) => j === i ? { ...x, ...patch } : x));
  const rmType = (i) => setTypes((t) => t.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.venue || !form.event_date) return toast.error("Champs obligatoires manquants");
    if (types.length === 0) return toast.error("Ajoutez au moins un type de billet");
    setBusy(true);
    try {
      const { data } = await api.post("/events", { ...form, ticket_types: types });
      toast.success("Événement créé — n'oubliez pas de le publier");
      navigate(`/artist/events/${data.event.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="new-event-page" className="max-w-3xl mx-auto">
      <Link to="/artist/events" className="inline-flex items-center text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Retour
      </Link>
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Créer un événement</h1>
      <form onSubmit={submit} className="space-y-6">
        <div className="card-mc p-6 space-y-4">
          <p className="overline">1. Informations</p>
          <input data-testid="event-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre de l'événement (ex. Concert Kankan Live)" className="input-mc w-full" />
          <textarea data-testid="event-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description détaillée…" rows={4} className="input-mc w-full resize-none" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="event-venue" required value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Lieu (ex. Palais du peuple)" className="input-mc" />
            <input data-testid="event-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="input-mc" />
          </div>
          <input data-testid="event-date" required type="datetime-local" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="input-mc w-full" />
          <ImageUpload testid="event-cover-upload" label="Image de couverture" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} />
        </div>

        <div className="card-mc p-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="overline">2. Types de billets</p>
            <button type="button" onClick={addType} data-testid="add-type-btn" className="btn-outline text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
            </button>
          </div>
          {types.map((t, i) => (
            <div key={i} className="border border-border p-4 space-y-2" data-testid={`type-row-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <input required value={t.name} onChange={(e) => updType(i, { name: e.target.value })} placeholder="Nom (Standard, VIP…)" className="input-mc" data-testid={`type-name-${i}`} />
                <input required type="number" min={0} value={t.price_gnf} onChange={(e) => updType(i, { price_gnf: Number(e.target.value) })} placeholder="Prix GNF" className="input-mc" data-testid={`type-price-${i}`} />
                <input required type="number" min={1} value={t.quantity_total} onChange={(e) => updType(i, { quantity_total: Number(e.target.value) })} placeholder="Nombre de places" className="input-mc" data-testid={`type-qty-${i}`} />
                <button type="button" onClick={() => rmType(i)} className="btn-ghost text-destructive text-xs" disabled={types.length === 1} data-testid={`type-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <input value={t.description} onChange={(e) => updType(i, { description: e.target.value })} placeholder="Description (optionnel)" className="input-mc w-full text-xs" />
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">Commission MCZ EVENTS : 3% sur chaque billet vendu — vous conservez 97%.</p>
        </div>

        <button data-testid="submit-event-btn" type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Créer l'événement
        </button>
      </form>
    </div>
  );
}

function ManageEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [scannerLabel, setScannerLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLive, setShowLive] = useState(false);

  const load = () => api.get(`/events/${id}/manage`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;

  const { event, ticket_types, scanners, stats } = data;

  const publish = async () => {
    setBusy(true);
    try {
      await api.patch(`/events/${id}`, { published: !event.published });
      toast.success(event.published ? "Événement dépublié" : "Événement publié !");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Supprimer cet événement ? (uniquement si aucun billet n'a été vendu)")) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Événement supprimé");
      navigate("/artist/events");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addScanner = async () => {
    if (!scannerLabel.trim()) return toast.error("Nom du contrôleur requis");
    try {
      const { data: r } = await api.post(`/events/${id}/scanners`, { label: scannerLabel.trim() });
      toast.success(`Code créé : ${r.scanner.code}`);
      setScannerLabel("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeScanner = async (sid) => {
    if (!confirm("Révoquer ce code contrôleur ?")) return;
    try { await api.post(`/scanners/${sid}/revoke`); toast.success("Code révoqué"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success("Code copié"));
  };

  const scannerUrl = (code) => `${window.location.origin}/scan/${code}`;

  return (
    <div data-testid="manage-event-page" className="max-w-5xl mx-auto">
      <Link to="/artist/events" className="inline-flex items-center text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Retour
      </Link>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
        <div>
          <p className="overline mb-1">{event.published ? "🟢 Publié" : "⚪ Brouillon"}</p>
          <h1 className="font-display font-black text-3xl tracking-tighter">{event.title}</h1>
          <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {new Date(event.event_date).toLocaleString("fr-FR")}</p>
          <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {event.venue} · {event.city}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {event.published && (
            <ShareButton
              testid="share-my-event-btn"
              url={`${process.env.REACT_APP_BACKEND_URL}/api/e/${event.id}`}
              title={event.title}
              text={`🎫 ${event.title} — ${event.artist_name}\n📅 ${new Date(event.event_date).toLocaleDateString("fr-FR")}\n📍 ${event.venue}, ${event.city}\nRéservez vos billets sur MCZ EVENTS :`}
            />
          )}
          <button data-testid="live-entries-btn" onClick={() => setShowLive((s) => !s)} className={showLive ? "btn-primary" : "btn-secondary"}>
            <Radio className="w-4 h-4 mr-2" /> Entrées en direct
          </button>
          <button data-testid="publish-toggle-btn" onClick={publish} disabled={busy} className={event.published ? "btn-outline" : "btn-primary"}>
            {event.published ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {event.published ? "Dépublier" : "Publier"}
          </button>
          <button data-testid="delete-event-btn" onClick={remove} className="btn-ghost text-destructive">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Billets vendus" value={stats.sold} icon={Ticket} />
        <StatCard label="Recettes brutes" value={formatGNF(stats.revenue_gnf)} icon={BarChart3} />
        <StatCard label="Commission 3%" value={formatGNF(stats.commission_gnf)} icon={UsersIcon} />
        <StatCard label="Scannés à l'entrée" value={`${stats.scanned} / ${stats.sold}`} icon={ScanLine} />
      </div>

      {showLive && (
        <div className="card-mc p-6 mb-6" data-testid="artist-live-stats-section">
          <h2 className="font-display font-black text-xl tracking-tighter mb-4">Entrées en direct</h2>
          <LiveEntryStats eventId={id} />
        </div>
      )}

      <div className="card-mc p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-display font-black text-xl tracking-tighter">Types de billets</h2>
          <Link to={`/events/${id}`} target="_blank" className="text-xs font-bold underline">Voir la page publique →</Link>
        </div>
        <div className="space-y-2">
          {ticket_types.map((t) => (
            <div key={t.id} className="border border-border p-3 flex flex-wrap justify-between items-center gap-3 text-sm">
              <div>
                <p className="font-bold">{t.name} · {formatGNF(t.price_gnf)}</p>
                {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
              </div>
              <div className="text-xs font-mono">
                {t.quantity_sold} / {t.quantity_total} vendus
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-mc p-6">
        <div className="flex items-center gap-2 mb-1">
          <ScanLine className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h2 className="font-display font-black text-xl tracking-tighter">Contrôleurs à l'entrée</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Créez un code unique par contrôleur. Il pourra scanner les billets sans compte via <code className="font-mono text-foreground bg-muted px-1">/scan/CODE</code> — utilisable directement dans le navigateur du téléphone.</p>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input data-testid="scanner-label-input" value={scannerLabel} onChange={(e) => setScannerLabel(e.target.value)} placeholder="Nom (ex. Portier 1, Karim)" className="input-mc flex-1" />
          <button data-testid="add-scanner-btn" onClick={addScanner} className="btn-primary"><Plus className="w-4 h-4 mr-1" /> Créer un code</button>
        </div>
        {scanners.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Aucun contrôleur créé.</p>
        ) : (
          <div className="space-y-2">
            {scanners.map((s) => (
              <div key={s.id} data-testid={`scanner-row-${s.id}`} className={`border border-border p-3 flex flex-wrap justify-between items-center gap-2 text-sm ${s.revoked ? "opacity-50" : ""}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold">{s.label} {s.revoked && <span className="text-destructive text-xs">(révoqué)</span>}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="font-mono text-lg font-black tracking-widest">{s.code}</code>
                    <button data-testid={`copy-code-${s.id}`} onClick={() => copyCode(s.code)} className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="text-[10px] font-mono text-muted-foreground mt-1 break-all">{scannerUrl(s.code)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono">{s.scans_count} scans</span>
                  {!s.revoked && <button data-testid={`revoke-${s.id}`} onClick={() => revokeScanner(s.id)} className="btn-ghost text-destructive text-xs">Révoquer</button>}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Scanner directement depuis votre compte artiste :</p>
          <Link to={`/artist/scan?event=${id}`} className="btn-outline text-sm" data-testid="artist-scan-btn">
            <ScanLine className="w-4 h-4 mr-2" /> Ouvrir le scanner (artiste)
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-mc p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="overline">{label}</p>
      </div>
      <p className="font-display font-black text-2xl tracking-tighter">{value}</p>
    </div>
  );
}
